import React, { useState, useEffect } from 'react';
import {
  Container,
  Header,
  Segment,
  Table,
  Label,
  Icon,
  Menu,
  Message,
  Accordion,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, getAuthHeaders } from '../api';

const METHOD_COLORS = {
  GET: 'blue',
  POST: 'green',
  PUT: 'orange',
  PATCH: 'yellow',
  DELETE: 'red',
};

export default function ApiDocs() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeModule, setActiveModule] = useState(null);

  useEffect(() => {
    async function fetchDocs() {
      try {
        const res = await fetch(`${getApiPath()}/@polyglot/api-docs`, {
          headers: getAuthHeaders(),
        });
        if (!res.ok) throw new Error('Failed to load API documentation');
        const json = await res.json();
        setData(json);
        if (json.modules && json.modules.length > 0) {
          setActiveModule(json.modules[0].id);
        }
      } catch (err) {
        setError(err.message);
      }
      setLoading(false);
    }
    fetchDocs();
  }, []);

  if (loading) {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Segment loading placeholder style={{ minHeight: '300px' }} />
      </Container>
    );
  }

  if (error) {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Message error>{error}</Message>
      </Container>
    );
  }

  if (!data) return null;

  const currentModule = data.modules.find((m) => m.id === activeModule);

  return (
    <Container style={{ paddingTop: '2em' }}>
      <Helmet title="API Reference" />

      <div className="polyglot-page-header">
        <h1>API Reference</h1>
        <p className="subtitle">
          {data.total_endpoints} endpoints across {data.total_modules} modules
        </p>
      </div>

      {/* Authentication info */}
      <Segment secondary>
        <Header as="h4">
          <Icon name="lock" /> Authentication
        </Header>
        <p>
          All endpoints (except those marked "public") require a JWT Bearer
          token. Obtain a token via{' '}
          <code>POST /++api++/@login</code> with{' '}
          <code>{`{"login": "username", "password": "..."}`}</code>.
          Tokens expire after {data.authentication.expiry}.
        </p>
        <p>
          <strong>Header:</strong>{' '}
          <code>{data.authentication.header}</code>
        </p>
        <p>
          <strong>Base URL:</strong>{' '}
          <code>{data.base_url}</code>
        </p>
      </Segment>

      <div style={{ display: 'flex', gap: '1.5em', marginTop: '1.5em' }}>
        {/* Module navigation */}
        <div style={{ width: '220px', flexShrink: 0 }}>
          <Menu vertical fluid size="small">
            <Menu.Item header>Modules</Menu.Item>
            {data.modules.map((m) => (
              <Menu.Item
                key={m.id}
                active={activeModule === m.id}
                onClick={() => setActiveModule(m.id)}
              >
                <Label color={m.color} size="tiny" style={{ float: 'right' }}>
                  {m.endpoints.length}
                </Label>
                <Icon name={m.icon} color={m.color} />
                {m.title}
              </Menu.Item>
            ))}
          </Menu>
        </div>

        {/* Endpoint detail */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {currentModule && (
            <>
              <Header as="h2">
                <Icon name={currentModule.icon} color={currentModule.color} />
                <Header.Content>
                  {currentModule.title}
                  <Header.Subheader>{currentModule.description}</Header.Subheader>
                </Header.Content>
              </Header>

              <p style={{ color: '#888', marginBottom: '1em' }}>
                <Icon name="file code" /> Source: <code>{currentModule.file}</code>
                {' · '}
                {currentModule.endpoints.length} endpoint{currentModule.endpoints.length !== 1 ? 's' : ''}
              </p>

              <Table celled structured>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell width={2}>Method</Table.HeaderCell>
                    <Table.HeaderCell width={5}>Endpoint</Table.HeaderCell>
                    <Table.HeaderCell width={3}>Permission</Table.HeaderCell>
                    <Table.HeaderCell width={6}>Description</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {currentModule.endpoints.map((ep, i) => (
                    <Table.Row key={i}>
                      <Table.Cell>
                        <Label
                          color={METHOD_COLORS[ep.method] || 'grey'}
                          size="small"
                          style={{ fontFamily: 'monospace', minWidth: '55px', textAlign: 'center' }}
                        >
                          {ep.method}
                        </Label>
                      </Table.Cell>
                      <Table.Cell>
                        <code style={{ fontSize: '0.9em', wordBreak: 'break-all' }}>
                          {ep.path}
                        </code>
                      </Table.Cell>
                      <Table.Cell>
                        {ep.permission === 'None (public)' ? (
                          <Label size="tiny" color="green">Public</Label>
                        ) : (
                          <Label size="tiny" basic>{ep.permission}</Label>
                        )}
                      </Table.Cell>
                      <Table.Cell style={{ fontSize: '0.9em' }}>
                        {ep.description}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </>
          )}
        </div>
      </div>
    </Container>
  );
}
